<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
  addDays,
  endOfDay,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
  addMonths,
} from 'date-fns';
import { useCalendarEventsStore } from 'dashboard/stores/calendarEvents';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import CalendarMonthGrid from 'dashboard/components-next/Calendar/CalendarMonthGrid.vue';
import CalendarSourceManageDialog from 'dashboard/components-next/Calendar/CalendarSourceManageDialog.vue';
import CalendarEventDetailDialog from 'dashboard/components-next/Calendar/CalendarEventDetailDialog.vue';
import CalendarMoveEventConfirmDialog from 'dashboard/components-next/Calendar/CalendarMoveEventConfirmDialog.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const calendarEventsStore = useCalendarEventsStore();

const currentDate = ref(startOfMonth(new Date()));
const manageDialogRef = ref(null);
const eventDetailDialogRef = ref(null);
const moveConfirmDialogRef = ref(null);
const selectedEvent = ref({});
const pendingMove = ref(null);
const isMovingEvent = ref(false);

const events = computed(() => calendarEventsStore.records);
const isFetchingList = computed(
  () => calendarEventsStore.getUIFlags.fetchingList
);

const visibleRange = computed(() => {
  const start = startOfWeek(startOfMonth(currentDate.value), {
    weekStartsOn: 1,
  });
  const end = addDays(start, 41);
  return { since: startOfDay(start), until: endOfDay(end) };
});

const headerLabel = computed(() => format(currentDate.value, 'MMMM yyyy'));

const fetchEvents = () => {
  const { since, until } = visibleRange.value;
  calendarEventsStore.fetchRange(
    format(since, 'yyyy-MM-dd'),
    format(until, 'yyyy-MM-dd')
  );
};

const goToPreviousMonth = () => {
  currentDate.value = subMonths(currentDate.value, 1);
};

const goToNextMonth = () => {
  currentDate.value = addMonths(currentDate.value, 1);
};

const goToToday = () => {
  currentDate.value = startOfMonth(new Date());
};

const openManageDialog = () => {
  manageDialogRef.value?.open();
};

const handleSelectEvent = event => {
  if (event.sourceType === 'contact_attribute') {
    router.push({
      name: 'contacts_edit',
      params: { accountId: route.params.accountId, contactId: event.contactId },
    });
    return;
  }
  if (event.sourceType === 'follow_up') {
    router.push({
      name: 'inbox_conversation',
      params: {
        accountId: route.params.accountId,
        conversation_id: event.conversationId,
      },
    });
    return;
  }
  selectedEvent.value = event;
  eventDetailDialogRef.value?.open();
};

const handleMoveEvent = ({ event, fromDay, toDay }) => {
  if (event.sourceType !== 'contact_attribute') return;

  pendingMove.value = { event, fromDay, toDay };
  moveConfirmDialogRef.value?.open();
};

const confirmMove = async () => {
  const { event, toDay } = pendingMove.value;
  isMovingEvent.value = true;
  try {
    await calendarEventsStore.moveContactAttributeEvent({
      eventId: event.id,
      contactId: event.contactId,
      attributeKey: event.attributeKey,
      newDate: format(toDay, 'yyyy-MM-dd'),
    });
  } catch (error) {
    useAlert(t('CALENDAR.MOVE_EVENT.ERROR'));
  } finally {
    isMovingEvent.value = false;
    pendingMove.value = null;
    moveConfirmDialogRef.value?.close();
  }
};

const cancelMove = () => {
  pendingMove.value = null;
  fetchEvents();
};

watch(currentDate, fetchEvents);
onMounted(fetchEvents);
</script>

<template>
  <div class="flex flex-col w-full h-full gap-4 p-4 overflow-y-auto">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <h1 class="mb-0 text-lg font-medium text-n-slate-12">
          {{ t('CALENDAR.HEADER') }}
        </h1>
        <span class="text-sm text-n-slate-11">{{ headerLabel }}</span>
      </div>
      <div class="flex items-center gap-2">
        <Button
          :label="t('CALENDAR.TODAY')"
          variant="faded"
          color="slate"
          size="sm"
          @click="goToToday"
        />
        <Button
          icon="i-lucide-chevron-left"
          variant="faded"
          color="slate"
          size="sm"
          @click="goToPreviousMonth"
        />
        <Button
          icon="i-lucide-chevron-right"
          variant="faded"
          color="slate"
          size="sm"
          @click="goToNextMonth"
        />
        <Button
          :label="t('CALENDAR.MANAGE_CALENDARS')"
          icon="i-lucide-settings"
          color="slate"
          size="sm"
          @click="openManageDialog"
        />
      </div>
    </div>

    <span v-if="isFetchingList" class="text-sm text-n-slate-11">
      {{ t('CALENDAR.LOADING') }}
    </span>

    <CalendarMonthGrid
      :current-date="currentDate"
      :events="events"
      :disabled="isFetchingList"
      @select-event="handleSelectEvent"
      @move-event="handleMoveEvent"
    />

    <CalendarSourceManageDialog
      ref="manageDialogRef"
      @sources-updated="fetchEvents"
    />
    <CalendarEventDetailDialog
      ref="eventDetailDialogRef"
      :event="selectedEvent"
    />
    <CalendarMoveEventConfirmDialog
      ref="moveConfirmDialogRef"
      :pending-move="pendingMove"
      :is-loading="isMovingEvent"
      @confirm="confirmMove"
      @close="cancelMove"
    />
  </div>
</template>
